import React, { useState } from 'react';
import { Icon } from '@fikasio/react-ui-components';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import AddTimeEntryModal from 'components/tasks/AddTimeEntryModal';
import TrashIcon from 'components/UI/TrashIcon';
import { useAuth } from 'services/login/hooks';
import { useCurrentOrganizationUsersQuery } from 'services/organizations/api';
import { usePatchTaskMutation } from 'services/tasks/api';
import { Task, TimeEntry } from 'services/tasks/types';
import { assigneeDisplayName, withCurrentAssignableUser } from 'services/users/assignable';
import {
  entryForSave,
  formatEntryDate,
  formatLoggedHours,
  sortTimeEntries,
  timeEntryLabelValue,
  totalMinutes,
} from './timeLog';
import styles from './TaskTimeEntries.module.css';

interface TaskTimeEntriesProps {
  task: Task;
}

const entryWhen = (entry: TimeEntry) => (
  entry.startTime
    ? `${formatEntryDate(entry.startTime)} – ${formatEntryDate(entry.endTime)}`
    : formatEntryDate(timeEntryLabelValue(entry))
);

const TaskTimeEntries = ({ task }: TaskTimeEntriesProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: assignableUsers } = useCurrentOrganizationUsersQuery();
  const users = withCurrentAssignableUser(assignableUsers, user);
  const [patchTask] = usePatchTaskMutation();
  const [editing, setEditing] = useState<TimeEntry | 'new' | null>(null);
  const taskId = task.id;
  const entries = sortTimeEntries(task.timeEntries ?? []);
  const loggedMinutes = totalMinutes(entries);

  if (!taskId) {
    return null;
  }

  const nameFor = (userId?: string) => {
    if (!userId || !assignableUsers) {
      return undefined;
    }
    const match = users.find(person => person.id === userId);
    return match ? assigneeDisplayName(match) : t('unknownUser');
  };

  const saveEntries = (timeEntries: TimeEntry[]) => {
    patchTask({
      id: taskId,
      timeEntries,
    });
  };

  return (
    <div>
      <div className={styles.toolbar}>
        {
          loggedMinutes > 0 && (
            <div className={styles.total}>
              {t('timeLogged')}: {formatLoggedHours(loggedMinutes)}
            </div>
          )
        }
        <Button
          onClick={() => setEditing('new')}
          size="sm"
          type="button"
          variant="outline-primary"
        >
          {t('addTimeEntry')}
        </Button>
      </div>
      {
        entries.length === 0 && (
          <div className={styles.empty}>{t('noTimeEntries')}</div>
        )
      }
      <ul className={styles.list}>
        {
          entries.map(entry => {
            const when = entryWhen(entry);
            const userName = nameFor(entry.userId);
            return (
              <li
                className={styles.entry}
                key={entry.id}
              >
                <div className={styles.top}>
                  <div className={styles.when}>{when}</div>
                  <div className={styles.actions}>
                    <button
                      aria-label={`${t('edit')} ${when}`}
                      className={styles.action}
                      onClick={() => setEditing(entry)}
                      title={t('edit')}
                      type="button"
                    >
                      <Icon name="edit" size="1x" />
                    </button>
                    <button
                      aria-label={`${t('delete')} ${when}`}
                      className={styles.action}
                      onClick={() => {
                        saveEntries(
                          (task.timeEntries ?? [])
                            .filter(item => item.id !== entry.id)
                            .map(entryForSave),
                        );
                      }}
                      title={t('delete')}
                      type="button"
                    >
                      <TrashIcon size="1x" />
                    </button>
                  </div>
                </div>
                <div className={styles.meta}>
                  {
                    typeof entry.minutes === 'number' && (
                      <span>{formatLoggedHours(entry.minutes)}</span>
                    )
                  }
                  {
                    entry.estimatedRemainingTime !== undefined && (
                      <span>
                        {t('timeLeft')}: {
                          entry.estimatedRemainingTime == null
                            ? '—'
                            : formatLoggedHours(entry.estimatedRemainingTime)
                        }
                      </span>
                    )
                  }
                  {userName && <span>{userName}</span>}
                </div>
                {entry.note && <div className={styles.note}>{entry.note}</div>}
              </li>
            );
          })
        }
      </ul>
      {
        editing && (
          <AddTimeEntryModal
            entry={editing === 'new' ? undefined : editing}
            key={editing === 'new' ? 'new' : editing.id}
            onHide={() => setEditing(null)}
            onSave={patch => saveEntries(patch.timeEntries)}
            show
            task={task}
          />
        )
      }
    </div>
  );
};

export default TaskTimeEntries;
